// src/modules/app-views/services/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import ServicesInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import servicesInitActionButton from 'modules/app-views/services/pages/Init/actions/actionButton';
import servicesInitMenuButton from 'modules/app-views/services/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import ServicesDocument from 'modules/app-views/services/pages/Document';
import servicesDocumentActionButton from 'modules/app-views/services/pages/Document/actions/actionButton';
import servicesDocumentMenuButton from 'modules/app-views/services/pages/Document/actions/menuButton';
import servicesDocumentDecorateRequest from './pages/Document/decorators/request';
import ServicesAi from 'modules/app-views/services/pages/Ai';
import aiGenerativeIcon from 'assets/images/ai-generative.png';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import ServicesDocumentCardHeaderColumn1 from 'modules/app-views/services/pages/Document/components/ServicesDocumentCardHeaderColumn1';
import ServicesDocumentCardHeaderColumn2 from 'modules/app-views/services/pages/Document/components/ServicesDocumentCardHeaderColumn2';
import ServicesDocumentCardHeaderColumn3 from 'modules/app-views/services/pages/Document/components/ServicesDocumentCardHeaderColumn3';
import ServicesParametersModal from 'modules/app-views/services/pages/Init/modals/ServicesParametersModal';
import seedServicesParameters from 'modules/app-views/services/seed';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'services';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: ServicesParametersModal,
  parametersSeed: seedServicesParameters,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: 'services_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list', // 'page' | 'list' | 'document'
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <ServicesInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.SERVICES.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },


    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'documentCode', 'status', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default', // 'default' | 'card'
      actions: ['filter', 'refresh', servicesInitActionButton, servicesInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  ai: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'page',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props} hideBackToHomeLink={true}>
        <ServicesAi />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: null,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: false,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: false,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/ai',
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {},

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        showHighlightTitle: 'new_service',
        showHighlightIcon: aiGenerativeIcon,
      },

      card: {
        title: () => null,
        subtitle: () => null,
        column1: () => null,
        column2: () => null,
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <ServicesDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.SERVICES.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: servicesDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        servicesDocumentActionButton(),
        servicesDocumentMenuButton(),
        'return',
      ],
      viewMode: 'auto',
      backPage: 'init',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'

      avatar: {
        visible: false,
        placeholder: 'service',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} name={props?.name} />,
        subtitle: props => <CardHeaderSubtitle {...props} fieldName="sku" />,

        column1: props => <ServicesDocumentCardHeaderColumn1 {...props} />,
        column2: props => <ServicesDocumentCardHeaderColumn2 {...props} />,
        column3: props => <ServicesDocumentCardHeaderColumn3 {...props} />,

        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {
    name: 'Desenvolvimento de Software',
    sku: 'SRV001',
    price: 150.00,
    cnae: '6201500',
    lc116Code: '01.01',
    issTaxRate: 5,
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
